import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Select } from "antd";
import {
    AlertTriangle, ChevronLeftIcon, Download, IndianRupee, Info, RotateCcw,
    Save, Search, Settings2, ShieldAlert, TrendingUp, Users,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { PAYROLL_TDS_PREVIEW } from "../../constants/api";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

/**
 * TDS Preview — the dry run before payroll.
 *
 * Shows what payroll WOULD deduct from every employee this month and the full
 * projection behind each figure. Nothing is written to the ledger and no payslip
 * is created: this is the review gate that makes computing statutory amounts for
 * thousands of people safe rather than reckless. Export it, have your CA sign it
 * off, then run payroll.
 *
 * HR can force a figure per employee (the override escape hatch) for the cases
 * no engine should guess — arrears adjustments, a proof accepted after the
 * cut-off, a court-ordered variation. Clearing the box hands the employee back
 * to the engine.
 */

const MONTH_OPTIONS = moment.months().map((m, i) => ({ value: i + 1, label: m }));

const WARN_OPTIONS = [
    { value: "all", label: "All rows" },
    { value: "warnings", label: "Needs attention" },
    { value: "no_pan", label: "PAN missing / invalid" },
    { value: "override", label: "Manual override" },
    { value: "old_regime", label: "Old regime" },
];

const REGIME_OPTIONS = [
    { value: "new", label: "New (115BAC)" },
    { value: "old", label: "Old" },
];

// Shared antd control styling, matching the configuration screens.
const CONTROL_CLASS = cn(
    "w-full rounded-lg transition-colors duration-200",
    "border-gray-300 dark:border-gray-600",
    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20"
);

const INPUT_CLASS = cn(
    "rounded-md transition-colors duration-200",
    "border-gray-300 dark:border-gray-600",
    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20"
);

const TDSPreview = () => {
    const navigate = useNavigate();
    const { fetch, update } = useApiMethods();
    // Gated on its own key, not the general Payroll one: this screen exposes
    // every employee's salary and tax projection. `permission` = may view,
    // `edit` = may save overrides.
    const { permission: viewPermission, edit: editPermission } =
        checkUserPermissionFunc("payroll", "TDS Preview", "SubMenu");
    const { permission: configPermission } =
        checkUserPermissionFunc("payroll", "TDS Configuration", "SubMenu");

    const now = moment();
    const [payrollMonth, setPayrollMonth] = useState(now.month() + 1);
    const [payrollYear, setPayrollYear] = useState(now.year());

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [meta, setMeta] = useState(null);
    const [items, setItems] = useState([]);
    const [edits, setEdits] = useState({});   // employee_id -> { tds_amount, remarks, regime }
    const [search, setSearch] = useState("");
    const [deptFilter, setDeptFilter] = useState("all");
    const [warnFilter, setWarnFilter] = useState("all");
    const [expanded, setExpanded] = useState(null);

    const yearOptions = useMemo(
        () => [now.year() + 1, now.year(), now.year() - 1].map((y) => ({ value: y, label: String(y) })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const load = useCallback(async () => {
        if (!viewPermission) { setLoading(false); return; }
        const from = moment({ year: payrollYear, month: payrollMonth - 1, day: 1 });
        const res = await fetch(PAYROLL_TDS_PREVIEW, {
            payroll_month: payrollMonth,
            payroll_year: payrollYear,
            from_date: from.format("YYYY-MM-DD"),
            to_date: from.clone().endOf("month").format("YYYY-MM-DD"),
        }, setLoading);
        if (res?.status === "success") {
            setMeta(res.data);
            setItems(res.data.items || []);
            setEdits({});
        } else {
            ToastMsg("error", res?.message || "Could not load the TDS preview");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payrollMonth, payrollYear]);

    useEffect(() => { load(); }, [load]);

    const departments = useMemo(() => {
        const map = new Map();
        items.forEach((it) => { if (it.department_id) map.set(it.department_id, it.department_name || "-"); });
        return [
            { value: "all", label: "All departments" },
            ...Array.from(map, ([id, name]) => ({ value: String(id), label: name })),
        ];
    }, [items]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((it) => {
            const matchesSearch = !q
                || (it.employee_name || "").toLowerCase().includes(q)
                || (it.employee_code || "").toLowerCase().includes(q)
                || (it.pan || "").toLowerCase().includes(q);
            const matchesDept = deptFilter === "all" || String(it.department_id) === String(deptFilter);
            const matchesWarn =
                warnFilter === "all"
                || (warnFilter === "warnings" && it.warnings?.length > 0)
                || (warnFilter === "no_pan" && !it.has_valid_pan)
                || (warnFilter === "override" && it.source === "manual")
                || (warnFilter === "old_regime" && it.regime === "old");
            return matchesSearch && matchesDept && matchesWarn;
        });
    }, [items, search, deptFilter, warnFilter]);

    // Effective amount = the HR override if one is being typed, else the engine's.
    const effectiveAmount = (it) => {
        const edit = edits[it.employee_id];
        if (edit && edit.tds_amount !== undefined && edit.tds_amount !== "" && edit.tds_amount !== null) {
            return Number(edit.tds_amount);
        }
        return Number(it.tds_amount) || 0;
    };

    const liveTotal = useMemo(
        () => items.reduce((sum, it) => sum + effectiveAmount(it), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [items, edits]
    );

    const patchEdit = (employeeId, patch) =>
        setEdits((prev) => ({ ...prev, [employeeId]: { ...(prev[employeeId] || {}), ...patch } }));

    const clearOverride = (employeeId) => patchEdit(employeeId, { tds_amount: null, remarks: "" });

    const handleExport = async () => {
        const from = moment({ year: payrollYear, month: payrollMonth - 1, day: 1 });
        await fetch(PAYROLL_TDS_PREVIEW, {
            payroll_month: payrollMonth,
            payroll_year: payrollYear,
            from_date: from.format("YYYY-MM-DD"),
            to_date: from.clone().endOf("month").format("YYYY-MM-DD"),
            export_type: "excel",
        }, () => { });
    };

    const handleSave = async () => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to change payroll data"); return; }
        const payloadItems = Object.entries(edits)
            .map(([employeeId, edit]) => {
                const item = { employee_id: Number(employeeId) };
                if ("tds_amount" in edit) item.tds_amount = edit.tds_amount === "" ? null : edit.tds_amount;
                if (edit.remarks !== undefined) item.remarks = edit.remarks;
                if (edit.regime) item.regime = edit.regime;
                return item;
            })
            .filter((i) => "tds_amount" in i || i.regime);

        if (!payloadItems.length) { ToastMsg("error", "Nothing to save"); return; }

        const res = await update(PAYROLL_TDS_PREVIEW, {
            payroll_month: payrollMonth, payroll_year: payrollYear, items: payloadItems,
        }, false, setSaving);
        if (res?.status === "success") {
            ToastMsg("success", "Overrides saved");
            load();
        } else {
            ToastMsg("error", res?.message || "Could not save overrides");
        }
    };

    // Direct-URL guard. The sidebar already hides this screen without the
    // permission, but the route is reachable by typing the address.
    if (!viewPermission) {
        return (
            <ContainerCard scrollable>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-center py-20 text-center">
                    <ShieldAlert className="h-12 w-12 text-rose-500 mb-4" />
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No access to TDS Preview</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
                        This screen shows every employee's salary and tax projection, so it needs the
                        <strong> TDS Preview </strong> permission. Ask an administrator to grant it under
                        User Permissions &rarr; Payroll.
                    </p>
                    <Button
                        variant="outline"
                        onClick={() => navigate("/payroll/payrollprocess")}
                        className="mt-6 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        Back to Payroll
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex flex-col items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary mb-6"></div>
                <p className="text-md text-gray-700 dark:text-gray-300">Computing TDS projections...</p>
            </div>
        );
    }

    const totals = meta?.totals || {};
    const dirtyCount = Object.keys(edits).length;

    return (
        <ContainerCard scrollable>
            {/* Header */}
            <div className="flex items-center gap-4 mb-4 p-3">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={() => navigate("/payroll/payrollprocess")}
                />
                <div className="flex-1 min-w-0">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">TDS Preview</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        What payroll would deduct this month — review and export before running
                        {meta?.financial_year && <> &middot; FY {meta.financial_year}</>}
                    </p>
                </div>
                {configPermission && (
                    <Button
                        variant="outline"
                        onClick={() => navigate("/payroll/tdsconfiguration")}
                        className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        <Settings2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Configure slabs</span>
                    </Button>
                )}
            </div>

            {/* Period picker */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="space-y-2 w-40">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Payroll month</label>
                        <Select value={payrollMonth} onChange={setPayrollMonth} options={MONTH_OPTIONS}
                            className={CONTROL_CLASS} size="large" />
                    </div>
                    <div className="space-y-2 w-32">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Year</label>
                        <Select value={payrollYear} onChange={setPayrollYear} options={yearOptions}
                            className={CONTROL_CLASS} size="large" />
                    </div>
                    {meta?.enabled && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex-1 min-w-[220px] pb-2">
                            {meta.months_remaining} month{meta.months_remaining === 1 ? "" : "s"} left in the financial
                            year — outstanding tax is spread across these.
                        </p>
                    )}
                </div>
            </div>

            {/* Not configured */}
            {!meta?.enabled ? (
                <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-6 flex gap-3">
                    <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900 dark:text-amber-200">
                        <p className="font-semibold mb-1">TDS is not active for this period.</p>
                        <p className="mb-3">{meta?.reason || "No configuration found."}</p>
                        <p>
                            Payroll will run exactly as it does today and deduct <strong>zero</strong> tax — the engine never
                            guesses a rate. Enter the rates your CA confirms to activate it.
                        </p>
                        {configPermission ? (
                            <Button
                                onClick={() => navigate("/payroll/tdsconfiguration")}
                                variant="brand"
                                className="mt-4 flex items-center gap-2 shadow-sm"
                            >
                                <Settings2 className="w-4 h-4" /> Go to TDS Configuration
                            </Button>
                        ) : (
                            <p className="mt-4 font-medium">
                                You do not have the TDS Configuration permission — ask an administrator to set the rates.
                            </p>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {/* Summary */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <SummaryCard icon={Users} tint="blue" label="Employees" value={totals.employees ?? 0} />
                        <SummaryCard icon={IndianRupee} tint="violet" label="Total TDS this month"
                            value={`₹${Math.round(liveTotal).toLocaleString("en-IN")}`} />
                        <SummaryCard icon={ShieldAlert} tint="rose" label="PAN missing / invalid" value={totals.no_pan ?? 0} />
                        <SummaryCard icon={TrendingUp} tint="amber" label="Over 30% of gross" value={totals.high_ratio ?? 0} />
                    </div>

                    {/* Advisory */}
                    <div className="mb-4 rounded-lg border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-900/20 p-4 flex gap-3">
                        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-900 dark:text-blue-200 space-y-1">
                            <p>
                                Figures are projections at each employee's current gross. The payroll run applies the same
                                calculation, then clamps the amount so take-home never goes negative.
                            </p>
                            {totals.no_pan > 0 && (
                                <p className="font-medium">
                                    {totals.no_pan} employee{totals.no_pan === 1 ? " is" : "s are"} taxed at a flat 20% under
                                    section 206AA for want of a valid PAN — collect PANs before running to avoid that.
                                </p>
                            )}
                            {totals.no_salary > 0 && (
                                <p>{totals.no_salary} employee{totals.no_salary === 1 ? "" : "s"} skipped: no active salary structure.</p>
                            )}
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4">
                        <div className="flex flex-wrap items-end gap-4">
                            <div className="space-y-2 flex-1 min-w-[220px] max-w-sm">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Search</label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                                    <Input
                                        type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Name, employee code or PAN…"
                                        className={cn(INPUT_CLASS, "w-full pl-9")}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2 w-48">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Department</label>
                                <Select value={deptFilter} onChange={setDeptFilter} options={departments}
                                    className={CONTROL_CLASS} size="large" />
                            </div>
                            <div className="space-y-2 w-48">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Show</label>
                                <Select value={warnFilter} onChange={setWarnFilter} options={WARN_OPTIONS}
                                    className={CONTROL_CLASS} size="large" />
                            </div>
                            <div className="flex items-center gap-3 ml-auto pb-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                    {filtered.length} of {items.length}
                                </span>
                                <Button
                                    variant="outline" onClick={handleExport}
                                    className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    <Download className="w-4 h-4" />
                                    <span className="hidden sm:inline">Export for CA review</span>
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 flex flex-col min-h-0">
                        <div className="overflow-x-auto overflow-y-auto max-h-[min(62vh,680px)] overscroll-contain">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900/95">
                                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        <th className="px-4 py-3 font-medium">Employee</th>
                                        <th className="px-4 py-3 font-medium">PAN</th>
                                        <th className="px-4 py-3 font-medium">Regime</th>
                                        <th className="px-4 py-3 font-medium text-right">Monthly gross</th>
                                        <th className="px-4 py-3 font-medium text-right">Annual tax</th>
                                        <th className="px-4 py-3 font-medium text-right">Deducted YTD</th>
                                        <th className="px-4 py-3 font-medium text-right">TDS this month</th>
                                        <th className="px-4 py-3 font-medium">Override (₹)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                                                No employees match your search / filter.
                                            </td>
                                        </tr>
                                    ) : filtered.map((it) => {
                                        const edit = edits[it.employee_id] || {};
                                        const isOverridden = edit.tds_amount !== undefined && edit.tds_amount !== null && edit.tds_amount !== "";
                                        const isOpen = expanded === it.employee_id;
                                        return [
                                            <tr
                                                key={it.employee_id}
                                                className={cn("cursor-pointer transition-colors",
                                                    it.warnings?.length
                                                        ? "bg-amber-50/50 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                                        : "hover:bg-gray-50/60 dark:hover:bg-gray-700/30")}
                                                onClick={() => setExpanded(isOpen ? null : it.employee_id)}
                                            >
                                                <td className="px-4 py-3">
                                                    <div className="font-medium text-gray-900 dark:text-white">{it.employee_name}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{it.employee_code}</div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {it.has_valid_pan ? (
                                                        <span className="text-gray-700 dark:text-gray-300 font-mono text-xs">{it.pan}</span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                                                            <AlertTriangle className="h-3 w-3" /> {it.pan ? "Invalid" : "Missing"}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={cn("px-2 py-0.5 rounded-full text-[11px] font-medium",
                                                        it.regime === "old"
                                                            ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                                                            : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300")}>
                                                        {it.regime === "old" ? "Old" : "New"}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{money(it.monthly_gross)}</td>
                                                <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{money(it.annual_tax)}</td>
                                                <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{money(it.tds_deducted_ytd)}</td>
                                                <td className={cn("px-4 py-3 text-right font-semibold",
                                                    isOverridden ? "text-gray-400 line-through" : "text-gray-900 dark:text-white")}>
                                                    {money(it.tds_amount)}
                                                    {it.source === "manual" && (
                                                        <span className="block text-[11px] font-normal text-amber-600 dark:text-amber-400">saved override</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center gap-1.5">
                                                        <Input
                                                            type="number" min="0" step="100"
                                                            value={edit.tds_amount ?? ""}
                                                            placeholder="engine"
                                                            disabled={!editPermission}
                                                            onChange={(e) => patchEdit(it.employee_id, { tds_amount: e.target.value === "" ? "" : Number(e.target.value) })}
                                                            className={cn(INPUT_CLASS, "w-28 text-right")}
                                                        />
                                                        {(isOverridden || it.source === "manual") && (
                                                            <Button
                                                                variant="ghost" size="icon" disabled={!editPermission}
                                                                title="Clear override, use the engine"
                                                                onClick={() => clearOverride(it.employee_id)}
                                                                className="h-8 w-8 text-gray-400 hover:text-sidebar-primary hover:bg-gray-100 dark:hover:bg-gray-700"
                                                            >
                                                                <RotateCcw className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>,
                                            isOpen && (
                                                <tr key={`${it.employee_id}-detail`} className="bg-gray-50 dark:bg-gray-900/40">
                                                    <td colSpan={8} className="px-4 py-4">
                                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-3">
                                                            <Detail label="Projected annual gross" value={money(it.projected_annual_gross)} />
                                                            <Detail label="Taxable income" value={money(it.taxable_income)} />
                                                            <Detail label="Annual tax" value={money(it.annual_tax)} />
                                                            <Detail label="Outstanding" value={money(it.remaining_tax)} />
                                                            <Detail label="Spread over" value={`${it.months_remaining ?? "-"} month(s)`} />
                                                        </div>
                                                        {it.note && <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{it.note}</p>}
                                                        {it.warnings?.length > 0 && (
                                                            <ul className="space-y-1 mb-3">
                                                                {it.warnings.map((w, i) => (
                                                                    <li key={i} className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
                                                                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> <span>{w}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        <div className="flex flex-wrap items-end gap-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                                            <div className="space-y-2 w-44">
                                                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                                    Regime for this year
                                                                </label>
                                                                <Select
                                                                    value={edit.regime || it.regime}
                                                                    onChange={(value) => patchEdit(it.employee_id, { regime: value })}
                                                                    options={REGIME_OPTIONS}
                                                                    disabled={!editPermission}
                                                                    className={CONTROL_CLASS}
                                                                    size="large"
                                                                />
                                                            </div>
                                                            <div className="space-y-2 flex-1 min-w-[220px] max-w-md">
                                                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                                    Override remarks
                                                                </label>
                                                                <Input
                                                                    type="text" placeholder="Why this figure was forced"
                                                                    value={edit.remarks ?? ""}
                                                                    disabled={!editPermission}
                                                                    onChange={(e) => patchEdit(it.employee_id, { remarks: e.target.value })}
                                                                    className={cn(INPUT_CLASS, "w-full")}
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ),
                                        ];
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Footer actions */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <p className="text-xs text-gray-500 dark:text-gray-400 pl-2">
                            Nothing is deducted until you run payroll.
                            {dirtyCount > 0 && <strong className="text-amber-600 dark:text-amber-400"> {dirtyCount} unsaved change(s).</strong>}
                        </p>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline" onClick={load} disabled={saving}
                                className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                            >
                                <RotateCcw className="w-4 h-4" />
                                Recalculate
                            </Button>
                            <Button
                                variant="brand"
                                onClick={handleSave}
                                disabled={saving || !editPermission || dirtyCount === 0}
                                className={cn(
                                    "transition-colors duration-200 flex items-center gap-2",
                                    (saving || !editPermission || dirtyCount === 0) && "opacity-50 cursor-not-allowed"
                                )}
                            >
                                {saving
                                    ? <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                    : <Save className="w-4 h-4" />}
                                Save overrides
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </ContainerCard>
    );
};

const money = (v) => `₹${Math.round(Number(v) || 0).toLocaleString("en-IN")}`;

const TINTS = {
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
    amber: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
    rose: "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400",
    violet: "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400",
};

const SummaryCard = ({ icon: Icon, tint, label, value }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 truncate">{label}</p>
            <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
        </div>
        <div className={cn("w-11 h-11 rounded-lg flex items-center justify-center shrink-0", TINTS[tint])}>
            <Icon className="h-5 w-5" />
        </div>
    </div>
);

const Detail = ({ label, value }) => (
    <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 truncate">{label}</p>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{value}</p>
    </div>
);

export default TDSPreview;
